import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { searchAddresses, getPlaceDetails, generateSessionToken } from '../services/googlePlacesService';
import { useFlexibleTranslation } from '../contexts/LanguageContext';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  outline: none;
  
  &:focus {
    border-color: #007cba;
    box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.2);
  }
`;

const SuggestionsList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Suggestion = styled.div`
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const MainText = styled.div`
  font-weight: 500;
  color: #333;
`;

const SecondaryText = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 2px;
`;

const LoadingText = styled.div`
  padding: 12px;
  color: #666;
  font-style: italic;
`;

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect, 
  placeholder,
  disabled = false 
}) => {
  const { t } = useFlexibleTranslation();
  const defaultPlaceholder = t('addressAutocomplete.enterAddress', 'Enter address...');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionToken] = useState(() => generateSessionToken());
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (inputValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce the search
    timeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const results = await searchAddresses(inputValue, sessionToken);
        setSuggestions(results.predictions || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSuggestionClick = async (suggestion) => {
    try {
      setLoading(true);
      
      // Get place details for coordinates
      const details = await getPlaceDetails(suggestion.place_id, sessionToken);
      
      if (details.result) {
        const { formatted_address, geometry, address_components } = details.result;
        const countryComponent = (address_components || []).find(c => c.types?.includes('country'));
        const countryCode = countryComponent?.short_name || null; // 'CA', 'US', etc.

        
        onChange(formatted_address);
        
        if (onAddressSelect) {
          onAddressSelect({
            address: formatted_address,
            location: {
              lat: geometry.location.lat,
              lng: geometry.location.lng
            },
            placeId: suggestion.place_id,
            countryCode,   
            address_components  
          });
        }
      }
      
      setShowSuggestions(false);
      setSuggestions([]);
    } catch (error) {
      console.error('Error getting place details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <Container>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder || defaultPlaceholder}
        disabled={disabled}
      />
      
      {showSuggestions && (
        <SuggestionsList>
          {loading && (
            <LoadingText>{t('addressAutocomplete.searchingAddresses', 'Searching addresses...')}</LoadingText>
          )}
          
          {!loading && suggestions.length === 0 && (
            <LoadingText>{t('addressAutocomplete.noAddressesFound', 'No addresses found')}</LoadingText>
          )}
          
          {!loading && suggestions.map((suggestion) => (
            <Suggestion
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <MainText>{suggestion.structured_formatting?.main_text || suggestion.description}</MainText>
              <SecondaryText>{suggestion.structured_formatting?.secondary_text}</SecondaryText>
            </Suggestion>
          ))}
        </SuggestionsList>
      )}
    </Container>
  );
};

export default AddressAutocomplete; 